import { useMemo } from 'react';
import { useTicketsList, useAssignees } from '../hooks/useTicketsApi';
import { useAgentStatus } from '../hooks/useSettingsApi';
import { useOpsStats } from '../hooks/useAuditApi';
import { useAuth } from '../contexts/AuthContext';
import { useTicketFilters, toQueryString, toUrlString } from '../hooks/useTicketFilters';
import TicketFilterBar, { columnFilterChips } from '../components/TicketFilterBar';
import { rememberBoardUrl } from '../components/Layout';
import { FOCUS_RING } from '../components/ui/focus';
import TicketTable from '../components/TicketTable';
import { LoadError } from '../components/ui/LoadError';
import { LoadingAnnouncement } from '../components/ui/Skeleton';

/**
 * What the board shows. The API's own default — everything still outstanding —
 * stays the default here; `status` is only sent when the reader asks for
 * something else.
 *
 * Added 2026-08-28. Bruno, working the pilot: *"I do not see Ticket 00016
 * (because closed?)"* and *"In the ticket table maybe could be useful to make
 * closed ticket visible when requested."* The endpoint has accepted `?status=`
 * since P7 and nothing ever sent it, so a closed ticket was reachable only by
 * following a link from another ticket. "When requested" is the whole design:
 * the work queue is not quietly widened into an archive.
 *
 * P22 moved the selection into the URL. The three buttons now write the same
 * `status` parameter the filter chips do — one mechanism, one place to read the
 * current selection from, and a board a reviewer can send to a colleague.
 */
/** Mirrors `tickets/list.ts`'s DEFAULT_STATUSES and `Layout`'s own list — what
 *  "Open work" means when the board sends no `status` at all. */
const OPEN_STATUSES = ['New', 'NeedsReview', 'AwaitingSalesApproval', 'AwaitingApproval', 'AwaitingSupplier'];

const VIEWS = [
  { key: 'open', label: 'Open work', status: [] as string[], note: 'open' },
  { key: 'closed', label: 'Closed', status: ['Closed', 'Cancelled'], note: 'closed' },
  {
    key: 'all',
    label: 'All',
    status: ['New', 'NeedsReview', 'AwaitingSalesApproval', 'AwaitingApproval', 'AwaitingSupplier', 'Closed', 'Cancelled'],
    note: 'in total',
  },
] as const;

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');
}

export default function TicketBoard() {
  const { filters, view, setFilters, toggleIn, setOne, setSort, setColumnFilter, clear, activeCount } = useTicketFilters();
  const { user } = useAuth();

  // `toQueryString`, NOT the address bar: the URL also carries the sort and the
  // client column filters since P30, and sending those to the API would look
  // like a filter the server honoured.
  const query = useMemo(() => toQueryString(filters, false), [filters]);
  const { data: result, isLoading, isError, error, errorUpdatedAt, refetch } = useTicketsList(query);
  const tickets = result?.data;
  const { data: assignees } = useAssignees();

  // The threshold comes from the SERVER (DRA_STALE_TICKET_DAYS), so the badge
  // and the `TicketStaleReminder` ledger row can never disagree about what
  // "stale" means. While it is loading nothing is badged — better silent than
  // wrong on a number that is meant to prompt an action.
  const { data: agent } = useAgentStatus();
  const staleAfter = agent?.staleTicketDays ?? null;

  const activeView = VIEWS.find(v => sameSet(v.status, filters.status)) ?? null;

  /**
   * THE TOTAL THE EMPTY CELL STATES (P38, task 3 — §12, audit S1-03's other half)
   *
   * §12 wants the sentence *"No ticket matches these filters. There are 41 in
   * total."*, and the number is the hard part: `pagination.total` cannot supply it,
   * because `COUNT(*) OVER()` is a window over the same WHERE and reads 0 under the
   * very filter the sentence is explaining. That is the trap the headline fell into
   * and the audit warned about explicitly.
   *
   * So the figure comes from the ops counter in the header — a query that is
   * ALREADY in flight for every page (`Layout`'s `OpsStrip` mounts it, so this is a
   * cache read and not a second request), and one whose numbers are counted
   * server-side against the same statuses. The view's own selection decides which
   * of them to add: "Closed" must not be told there are 41, and "All" must not be
   * told there are 26.
   *
   * While the counter has not answered, there is no sentence. A total is a
   * measurement; §12's "never zeros" applies to it as much as to the toolbar.
   */
  const { data: stats } = useOpsStats();
  const viewTotal = useMemo(() => {
    if (!stats) return null;
    const by = stats.ticketsByStatus;
    const statuses = activeView ? activeView.status : filters.status;
    // "Open work" sends no status at all: the API's default is every non-terminal
    // one, which is what `OPEN_STATUSES` lists — the same set `Layout`'s counter
    // sums, so the empty cell and the header cannot disagree.
    const keys = statuses.length > 0 ? statuses : OPEN_STATUSES;
    return keys.reduce((sum, k) => sum + (by[k] ?? 0), 0);
  }, [stats, activeView, filters.status]);

  /**
   * WHAT THE NUMBER IN THE HEADLINE IS COUNTING (2026-09-29 design audit, S1-03)
   *
   * It used to read `${tickets.length} ${activeView.note}` — a FILTERED count
   * carrying the VIEW's noun. So a search that matched nothing printed
   * "0 open." on a board with 41 open tickets: a true number under a word that
   * made it a false statement about the system.
   *
   * `pagination.total` does not fix it and was the wrong idea: `COUNT(*) OVER()`
   * is a window over the same WHERE, so with a filter applied it is 0 as well.
   * What has to change is the NOUN. With no filter beyond the view itself,
   * "41 open" is true and stays. With one, the only honest thing the page can
   * say without a second query is that this is what the filter matched.
   *
   * The view's own `status` selection is subtracted, because the three buttons
   * write `status` like any filter does — without this, "Closed" would count as
   * two filters and the Closed board would stop saying "closed".
   */
  const filtersBeyondTheView = activeCount - (activeView?.status.length ?? 0);
  const countNoun = filtersBeyondTheView > 0 ? 'matching this filter' : (activeView?.note ?? 'matching');

  // The board hands the ticket screen its own URL for the same reason /review
  // does: "Back to tickets" that forgets which tickets is the same annoyance
  // whichever board it happens on. Only /review offers next/previous, because
  // only /review is a queue somebody works through in order.
  const origin = useMemo(() => `/tickets?${toUrlString(filters, view)}`, [filters, view]);

  /*
   * Task 9 (K9). The sidebar entry for this board is a static link and threw the
   * reader's filter away; it now follows the last URL this tab had here. The
   * value stored is exactly `origin`, which is the address bar's own form — so
   * the entry restores the filters, the sort AND the column filters, and there is
   * no second notion of "the board's state" to keep in step.
   *
   * It is written during render rather than in an effect on purpose: `origin` is
   * already a `useMemo` of the state that defines it, so this runs exactly when
   * that state changes, and a `sessionStorage` write is not an observable side
   * effect on the page. An effect would store the value one paint late, which is
   * the paint the reader clicks the sidebar in.
   */
  rememberBoardUrl('/tickets', origin);

  return (
    // `h-full` + a flex column, so the table below can take the height that is
    // left and scroll inside it — which is what makes its header stick (P37,
    // audit S1-08). `<main>` in `Layout.tsx` has a definite height, so the
    // percentage resolves; nothing here depends on a viewport calculation.
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Tickets</h1>
          <p className="mt-1 text-sm text-gray-500">
            One ticket per requested document. {tickets ? `${tickets.length} ${countNoun}.` : ''}
          </p>
        </div>
        {/* A named group: since P22 the status FILTER chips also carry a
            "Closed" label, and a control a screen reader cannot tell apart
            from another control is not labelled. */}
        <div role="group" aria-label="Ticket views" className="flex gap-1 rounded-lg border border-gray-200 bg-white p-0.5">
          {VIEWS.map(v => (
            <button
              key={v.key}
              // The view buttons own only `status`; every other filter, the
              // sort and the column filters survive a switch, because "the dch
              // tickets, now the closed ones" is a normal thing to want. That is
              // `setFilters` and not a hand-built URL — a hand-built one would
              // drop the view state the address bar now also carries.
              onClick={() => setFilters({ ...filters, status: [...v.status] })}
              aria-pressed={activeView?.key === v.key}
              // P40: §7's filter-chip rule (applied = an ink ground) on §2's tokens,
              // plus the house focus ring, which this segmented control never had.
              // Deliberately not the Button kit: a chip that reports a selection is
              // not a button that performs an action - same call as P39's
              // CompanyScopeButton.
              className={`h-8 rounded-ctl px-2.5 text-xs font-medium ${FOCUS_RING} ${
                activeView?.key === v.key ? 'bg-ink text-surface' : 'text-body hover:bg-hover'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <TicketFilterBar
        filters={filters}
        toggleIn={toggleIn}
        setOne={setOne}
        clear={clear}
        activeCount={activeCount}
        extraChips={columnFilterChips(view.columnFilters, setColumnFilter)}
      />

      {/* P38, task 2: the banner retries the QUERY, not the page — a reader's
          filters, sort and column layout survive a transient 502, which is the
          whole difference between a banner and "reload and start again". */}
      {isError && <LoadError what="Tickets" error={error} at={errorUpdatedAt} onRetry={refetch} />}
      {isLoading && <LoadingAnnouncement what="tickets" />}

      {/*
        * P38, task 5: the table renders THROUGH the load. It owns the toolbar, the
        * header band and the `<colgroup>`, so drawing skeleton rows inside it means
        * nothing on the page moves when the data arrives — where replacing the whole
        * table with the word `Loading…` made the reader watch it re-lay out.
        */}
      {!isError && (
        <TicketTable
          isLoading={isLoading}
          tickets={tickets ?? []}
          total={result?.pagination?.total}
          staleAfter={staleAfter}
          showReviewReason={false}
          origin={origin}
          storageKey="dra-tickets-col-widths"
          emptyMessage={activeCount > 0 ? 'No ticket matches these filters.' : 'Nothing outstanding.'}
          // Task 3. The sentence is the PAGE's because only the page knows what its
          // total counts — it has three views, and "41 in total" under the Closed
          // button would be a different 41. `pagination.total` cannot answer it
          // (see the headline's comment above): it is a window over the same WHERE.
          emptyTotalLabel={
            activeCount > 0 && viewTotal !== null
              ? `There ${viewTotal === 1 ? 'is' : 'are'} ${viewTotal} ${activeView?.note ?? 'matching'} in total.`
              : undefined
          }
          filters={filters}
          setFilters={setFilters}
          setOne={setOne}
          sort={view.sort}
          onSortChange={setSort}
          columnFilters={view.columnFilters}
          setColumnFilter={setColumnFilter}
          assignees={assignees ?? []}
          currentUserEmail={user?.email}
        />
      )}
    </div>
  );
}
